import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { formatIndianCurrency, formatIndianCurrencyCompact, formatIndianDate } from '../utils/format';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndianRupeeSign, faFolderOpen, faListCheck, faClipboardCheck, faUsers } from '@fortawesome/free-solid-svg-icons';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalProjects: 0,
    totalValue: 0,
    totalTasks: 0,
    pendingApprovals: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [projectsOverview, setProjectsOverview] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityPage, setActivityPage] = useState(1);
  const [activityLimit] = useState(10);
  const [activityTotal, setActivityTotal] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [alertsPage, setAlertsPage] = useState(1);
  const [alertsLimit] = useState(5);
  const [alertsTotal, setAlertsTotal] = useState(0);
  const [teamCount, setTeamCount] = useState(0);
  const [userCounts, setUserCounts] = useState({ totalUsers: 0, activeSales: 0, activeBdm: 0 });
  const [loading, setLoading] = useState(true);

  const StatCard = ({ title, value, fullValue, subtitle, icon, onClick }) => {
    return (
      <div className="stat-card" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
        <div className="stat-head">
          <div className="stat-icon" aria-hidden="true">
            <FontAwesomeIcon icon={icon} />
          </div>
        </div>
        <h3>{title}</h3>
        <div className="value" title={fullValue || undefined}>{value}</div>
        <div className="label">{subtitle}</div>
      </div>
    );
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchActivityPage = async (nextPage = activityPage) => {
    const res = await api.get('/api/activity', { params: { page: nextPage, limit: activityLimit } });
    const payload = res.data;
    if (payload && typeof payload === 'object' && Array.isArray(payload.items)) {
      setActivity(payload.items);
      setActivityTotal(payload.total || 0);
      setActivityPage(payload.page || nextPage);
    } else {
      const arr = Array.isArray(payload) ? payload : [];
      setActivity(arr.slice(0, activityLimit));
      setActivityTotal(arr.length);
      setActivityPage(1);
    }
  };

  const fetchAlertsPage = async (nextPage = alertsPage) => {
    const res = await api.get('/api/alerts', { params: { page: nextPage, limit: alertsLimit } });
    const payload = res.data;
    if (payload && typeof payload === 'object' && Array.isArray(payload.items)) {
      setAlerts(payload.items);
      setAlertsTotal(payload.total || 0);
      setAlertsPage(payload.page || nextPage);
    } else {
      const arr = Array.isArray(payload) ? payload : [];
      setAlerts(arr.slice(0, alertsLimit));
      setAlertsTotal(arr.length);
      setAlertsPage(1);
    }
  };

  const fetchAll = async () => {
    try {
      const requests = [
        api.get('/api/dashboard/stats'),
        api.get('/api/projects'),
        api.get('/api/activity', { params: { page: 1, limit: activityLimit } }),
        api.get('/api/alerts', { params: { page: 1, limit: alertsLimit } })
      ];

      if (user.role !== 'CLIENT') {
        requests.splice(2, 0, api.get('/api/tasks'));
      }

      if (user.role === 'ADMIN' || user.role === 'BDM') {
        requests.push(api.get('/api/users'));
      }

      const results = await Promise.all(requests);

      const statsRes = results[0];
      const projectsRes = results[1];
      const tasksRes = user.role !== 'CLIENT' ? results[2] : null;
      const activityRes = user.role !== 'CLIENT' ? results[3] : results[2];
      const alertsRes = user.role !== 'CLIENT' ? results[4] : results[3];
      const usersRes = user.role !== 'CLIENT' ? results[5] : null;

      setStats(statsRes.data);

      const projectsPayload = projectsRes.data;
      const projectsList = (projectsPayload && typeof projectsPayload === 'object' && Array.isArray(projectsPayload.items))
        ? projectsPayload.items
        : (Array.isArray(projectsPayload) ? projectsPayload : []);

      setRecentProjects(projectsList.slice(0, 5));
      setProjectsOverview(projectsList.slice(0, 8));
      const activityPayload = activityRes.data;
      if (activityPayload && typeof activityPayload === 'object' && Array.isArray(activityPayload.items)) {
        setActivity(activityPayload.items);
        setActivityTotal(activityPayload.total || 0);
        setActivityPage(activityPayload.page || 1);
      } else {
        const arr = Array.isArray(activityPayload) ? activityPayload : [];
        setActivity(arr.slice(0, activityLimit));
        setActivityTotal(arr.length);
        setActivityPage(1);
      }
      const alertsPayload = alertsRes.data;
      if (alertsPayload && typeof alertsPayload === 'object' && Array.isArray(alertsPayload.items)) {
        setAlerts(alertsPayload.items);
        setAlertsTotal(alertsPayload.total || 0);
        setAlertsPage(alertsPayload.page || 1);
      } else {
        const arr = Array.isArray(alertsPayload) ? alertsPayload : [];
        setAlerts(arr.slice(0, alertsLimit));
        setAlertsTotal(arr.length);
        setAlertsPage(1);
      }

      if (user.role === 'BDM' && usersRes) {
        setTeamCount((usersRes.data || []).length);
      }

      if (user.role === 'ADMIN' && usersRes) {
        const list = usersRes.data || [];
        setUserCounts({
          totalUsers: list.length,
          activeSales: list.filter((u) => u.role === 'SALES' && u.status === 'ACTIVE').length,
          activeBdm: list.filter((u) => u.role === 'BDM' && u.status === 'ACTIVE').length,
          activeDeveloper: list.filter((u) => u.role === 'DEVELOPER' && u.status === 'ACTIVE').length,
          activeClient: list.filter((u) => u.role === 'CLIENT' && u.status === 'ACTIVE').length,
          inactiveUsers: list.filter((u) => u.status === 'INACTIVE').length,
          adminUsers: list.filter((u) => u.role === 'ADMIN').length
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const roleTitle = useMemo(() => {
    if (user.role === 'ADMIN') return 'Admin Dashboard';
    if (user.role === 'BDM') return 'BDM Dashboard';
    if (user.role === 'DEVELOPER') return 'Developer Dashboard';
    if (user.role === 'CLIENT') return 'Client Dashboard';
    return 'Sales Dashboard';
  }, [user.role]);

  return (
    <div>
      <div className="flex items-start justify-between gap-6 mb-6">
        <div>
          <h1 className="text-2xl font-bold">{roleTitle}</h1>
          <p className="text-secondary mt-1">
            Welcome, <span className="font-semibold">{user.name}</span>
          </p>
        </div>
      </div>

      <div className="dashboard-stats">
        {loading ? (
          <>
            <div className="stat-card skeleton" />
            <div className="stat-card skeleton" />
            <div className="stat-card skeleton" />
            <div className="stat-card skeleton" />
            <div className="stat-card skeleton" />
          </>
        ) : user.role === 'CLIENT' ? (
          <>
            <StatCard title="Assigned Projects" value={stats.totalProjects || 0} subtitle="Projects in scope" icon={faFolderOpen} onClick={() => navigate('/projects')} />
            <StatCard title="Open Issues" value={stats.openIssues || 0} subtitle="Open issues in scope" icon={faClipboardCheck} onClick={() => navigate('/client-issues')} />
            <StatCard title="Total Issues" value={stats.totalIssues || 0} subtitle="All issues in scope" icon={faListCheck} onClick={() => navigate('/client-issues')} />
          </>
        ) : (
          <>
            <div className="stats-row-title">Business</div>
            {user.role !== 'DEVELOPER' && (
              <StatCard
                title={user.role === 'ADMIN' ? 'Total Pipeline' : 'My Pipeline'}
                value={formatIndianCurrencyCompact(stats.totalValue)}
                fullValue={formatIndianCurrency(stats.totalValue)}
                subtitle="Quoted amount in scope"
                icon={faIndianRupeeSign}
                onClick={() => navigate('/projects')}
              />
            )}
            <StatCard
              title={user.role === 'ADMIN' ? 'All Projects' : user.role === 'BDM' ? 'My Projects' : user.role === 'DEVELOPER' ? 'Assigned Projects' : 'My Projects'}
              value={stats.totalProjects}
              subtitle="Projects in scope"
              icon={faFolderOpen}
              onClick={() => navigate('/projects')}
            />

            <div className="stats-row-title">Operations</div>
            {user.role !== 'CLIENT' && user.role !== 'DEVELOPER' ? (
              <>
                <StatCard
                  title={user.role === 'ADMIN' ? 'All Tasks' : user.role === 'BDM' ? 'Project Tasks' : 'My Tasks'}
                  value={stats.totalTasks}
                  subtitle="Tasks in scope"
                  icon={faListCheck}
                  onClick={() => navigate('/tasks')}
                />
                <StatCard
                  title="Open Issues"
                  value={stats.openIssues || 0}
                  subtitle="Open client issues in scope"
                  icon={faClipboardCheck}
                  onClick={() => navigate('/client-issues')}
                />
                <StatCard
                  title="Total Issues"
                  value={stats.totalIssues || 0}
                  subtitle="All client issues in scope"
                  icon={faClipboardCheck}
                  onClick={() => navigate('/client-issues')}
                />
                <StatCard
                  title="Pending Approvals"
                  value={stats.pendingApprovals}
                  subtitle="Approvals in scope"
                  icon={faClipboardCheck}
                  onClick={() => navigate('/approvals')}
                />
              </>
            ) : null}

            {user.role === 'DEVELOPER' && (
              <>
                <StatCard
                  title="Project Tasks"
                  value={stats.totalTasks}
                  subtitle="Tasks in scope"
                  icon={faListCheck}
                  onClick={() => navigate('/tasks')}
                />
                <StatCard
                  title="Open Issues"
                  value={stats.openIssues || 0}
                  subtitle="Open client issues in scope"
                  icon={faClipboardCheck}
                  onClick={() => navigate('/client-issues')}
                />
                <StatCard
                  title="Total Issues"
                  value={stats.totalIssues || 0}
                  subtitle="All client issues in scope"
                  icon={faClipboardCheck}
                  onClick={() => navigate('/client-issues')}
                />
              </>
            )}

            {user.role === 'ADMIN' && (
              <StatCard
                title="Users"
                value={userCounts.totalUsers}
                subtitle={`Active : Admin:${userCounts.adminUsers || 0} | BDM:${userCounts.activeBdm || 0} | Sales:${userCounts.activeSales || 0} | Developer:${userCounts.activeDeveloper || 0} | Client:${userCounts.activeClient || 0} | Inactive:${userCounts.inactiveUsers || 0}`}
                icon={faUsers}
                onClick={() => navigate('/users')}
              />
            )}
            {user.role === 'BDM' && (
              <StatCard title="Users" value={teamCount} subtitle="Active mapped Sales users" icon={faUsers} onClick={() => navigate('/users')} />
            )}
          </>
        )}
      </div>

      {alerts.length > 0 && (
        <div className="glass-card p-6 mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Reminders</h2>
              <p className="text-secondary text-sm mt-1">You have {alertsTotal} due reminder(s).</p>
            </div>
            <button type="button" className="glass-button" onClick={() => navigate('/calendar')}>Open Calendar</button>
          </div>
          {alertsTotal > alertsLimit && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-xs text-tertiary">Page {alertsPage} of {Math.max(Math.ceil(alertsTotal / alertsLimit) || 1, 1)}</div>
              <div className="flex gap-2">
                <button type="button" className="glass-button" onClick={() => fetchAlertsPage(alertsPage - 1)} disabled={alertsPage <= 1}>
                  Prev
                </button>
                <button type="button" className="glass-button" onClick={() => fetchAlertsPage(alertsPage + 1)} disabled={alertsPage >= Math.max(Math.ceil(alertsTotal / alertsLimit) || 1, 1)}>
                  Next
                </button>
              </div>
            </div>
          )}
          <div className="mt-4 space-y-2">
            {alerts.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{a.title}</div>
                  <div className="text-xs text-tertiary truncate">
                    {a.project_title ? `${a.project_title} • ` : ''}{a.remind_at ? formatIndianDate(a.remind_at) : ''}
                  </div>
                </div>
                <span className="status-badge">Due</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="glass-card p-8 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Projects Overview</h2>
          <button type="button" className="glass-button" onClick={() => navigate('/projects')}>View All</button>
        </div>
        <div className="project-overview-list">
          {projectsOverview.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📁</div>
              <div className="empty-title">No projects yet</div>
              <div className="empty-sub">Create a project to start tracking pipeline and tasks.</div>
              {(user.role === 'ADMIN' || user.role === 'BDM') ? (
                <button type="button" className="glass-button primary" onClick={() => navigate('/projects', { state: { openForm: true } })}>
                  Create Project
                </button>
              ) : (
                <button type="button" className="glass-button" onClick={() => navigate('/projects')}>
                  View Projects
                </button>
              )}
            </div>
          ) : (
            projectsOverview.map((p) => {
              const lifecycle = p.is_active === 0 ? 'Closed' : String(p.status || '').toLowerCase() === 'onhold' ? 'Hold' : 'Active';
              const lifecycleClass = lifecycle.toLowerCase();
              const amountFull = formatIndianCurrency(p.quoted_amount || 0);
              const amountCompact = formatIndianCurrencyCompact(p.quoted_amount || 0);

              return (
                <div key={p.id} className="project-overview-row">
                  <div className="project-overview-main">
                    <div className="project-overview-title">{p.title}</div>
                    <div className="project-overview-meta">
                      <span className={`pill pill-${lifecycleClass}`}>{lifecycle}</span>
                      <span className="meta-dot">•</span>
                      <span className="meta-item">Client: {p.client_name || '—'}</span>
                      {p.owner_name ? (
                        <>
                          <span className="meta-dot">•</span>
                          <span className="meta-item">Owner: {p.owner_name}</span>
                        </>
                      ) : null}
                    </div>
                  </div>

                  <div className="project-overview-side">
                    {user.role !== 'CLIENT' ? (
                      <div className="project-overview-amount" title={amountFull}>{amountCompact}</div>
                    ) : null}
                    <div className="project-overview-actions">
                      <button type="button" className="glass-button text-sm px-3 py-1" onClick={() => navigate(`/projects`)}>
                        View
                      </button>
                      {(user.role === 'ADMIN' || user.role === 'BDM') ? (
                        <button type="button" className="glass-button text-sm px-3 py-1" onClick={() => navigate('/projects', { state: { editId: p.id } })}>
                          Edit
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-8">
        <div className="glass-card p-8">
          <h2 className="text-xl font-semibold mb-6">Quick Actions</h2>
          <div className="space-y-3">
            <button
              className="glass-button w-full py-3"
              type="button"
              onClick={() => navigate('/projects')}
            >
              View Projects
            </button>
            {user.role !== 'CLIENT' && user.role !== 'DEVELOPER' && (
              <>
                <button
                  className="glass-button w-full py-3"
                  type="button"
                  onClick={() => navigate('/tasks')}
                >
                  View Tasks
                </button>
                <button
                  className="glass-button w-full py-3"
                  type="button"
                  onClick={() => navigate('/approvals')}
                >
                  View Approvals
                </button>
              </>
            )}
            {user.role === 'DEVELOPER' && (
              <button
                className="glass-button w-full py-3"
                type="button"
                onClick={() => navigate('/tasks')}
              >
                View Tasks
              </button>
            )}

            <button
              className="glass-button w-full py-3"
              type="button"
              onClick={() => navigate('/client-issues')}
            >
              Client Issues
            </button>

            {(user.role === 'ADMIN' || user.role === 'BDM') && (
              <button
                className="glass-button primary w-full py-3"
                type="button"
                onClick={() => navigate('/projects', { state: { openForm: true } })}
              >
                Create Project
              </button>
            )}
            {user.role !== 'SALES' && user.role !== 'CLIENT' && (
              <button
                className="glass-button primary w-full py-3"
                type="button"
                onClick={() => navigate('/tasks', { state: { openForm: true } })}
              >
                Create Task
              </button>
            )}
            {user.role !== 'SALES' && user.role !== 'CLIENT' && user.role !== 'DEVELOPER' && (
              <button
                className="glass-button w-full py-3"
                type="button"
                onClick={() => navigate('/approvals', { state: { openForm: true } })}
              >
                Request Approval
              </button>
            )}
            {user.role === 'ADMIN' && (
              <button
                className="glass-button w-full py-3"
                type="button"
                onClick={() => navigate('/users', { state: { openForm: true } })}
              >
                Manage Users & Teams
              </button>
            )}
          </div>
        </div>

        <div className="glass-card p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Recent Activity</h2>
            <button type="button" className="glass-button" onClick={() => navigate('/calendar')}>Calendar</button>
          </div>
          {activityTotal > activityLimit && (
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs text-tertiary">Page {activityPage} of {Math.max(Math.ceil(activityTotal / activityLimit) || 1, 1)}</div>
              <div className="flex gap-2">
                <button type="button" className="glass-button" onClick={() => fetchActivityPage(activityPage - 1)} disabled={activityPage <= 1}>
                  Prev
                </button>
                <button
                  type="button"
                  className="glass-button"
                  onClick={() => fetchActivityPage(activityPage + 1)}
                  disabled={activityPage >= Math.max(Math.ceil(activityTotal / activityLimit) || 1, 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {activity.length === 0 ? (
              <div className="text-sm text-secondary">No activity found</div>
            ) : (
              activity.map((item) => (
                <div key={`${item.type}-${item.id}`} className="flex items-center justify-between p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{item.title}</div>
                    <div className="text-xs text-tertiary truncate">{item.subtitle || ''}{item.actor_name ? ` • ${item.actor_name}` : ''} • {formatIndianDate(item.ts)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.status ? <span className="status-badge">{item.status}</span> : null}
                    <button type="button" className="glass-button text-sm px-3 py-1" onClick={() => navigate(item.href || '/dashboard')}>Open</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
